import { supabase } from '@/lib/supabase'
import { fetchMaps } from './helpers'
import type { CategoryRef, ProfileInfo } from './helpers'
import type { Scope, Frequency, Currency, RecurringTransactionUpdate } from '@/lib/db-types'
import type { ScopeFilter } from '@/lib/utils'

export type RecurringTransaction = {
  id: string
  name: string
  amount: number
  currency: Currency
  category_id: string | null
  category_name: string | null
  parent_category_name: string | null
  parent_category_color: string | null
  day_of_month: number
  frequency: Frequency
  frequency_months: number[] | null
  paid_by: string
  paid_by_name: string | null
  paid_by_avatar_url: string | null
  payment_method_id: string | null
  is_active: boolean
  active_since: string | null
  scope: 'household' | 'personal'
  created_by: string
  created_at: string
}

export function mapRecurringTransaction(
  r: { id: string; name: string; amount: number; currency?: string; category_id: string | null; day_of_month: number; frequency: string; frequency_months: number[] | null; paid_by: string; payment_method_id?: string | null; is_active: boolean | null; active_since: string | null; scope: string; created_by?: string; created_at: string | null },
  profileMap: Map<string, ProfileInfo>,
  categoryMap: Map<string, CategoryRef>,
): RecurringTransaction {
  const category = r.category_id ? categoryMap.get(r.category_id) : null
  const parentCategory = category?.parent_id ? categoryMap.get(category.parent_id) : null
  const profile = profileMap.get(r.paid_by)
  return {
    id: r.id,
    name: r.name,
    amount: r.amount,
    currency: (r.currency as Currency) ?? 'JPY',
    category_id: r.category_id,
    category_name: category?.name ?? null,
    parent_category_name: parentCategory?.name ?? null,
    parent_category_color: parentCategory?.color ?? category?.color ?? null,
    day_of_month: r.day_of_month,
    frequency: r.frequency as Frequency,
    frequency_months: r.frequency_months,
    paid_by: r.paid_by,
    paid_by_name: profile?.display_name ?? null,
    paid_by_avatar_url: profile?.avatar_url ?? null,
    payment_method_id: r.payment_method_id ?? null,
    is_active: r.is_active ?? true,
    active_since: r.active_since,
    scope: r.scope as Scope,
    created_by: r.created_by ?? '',
    created_at: r.created_at!,
  }
}

export function buildRecurringUpdateData(data: RecurringUpdateData): RecurringTransactionUpdate {
  const updateData: RecurringTransactionUpdate = {}
  if (data.name !== undefined) updateData.name = data.name
  if (data.amount !== undefined) updateData.amount = data.amount
  if (data.currency !== undefined) updateData.currency = data.currency
  if (data.category_id !== undefined) updateData.category_id = data.category_id || null
  if (data.day_of_month !== undefined) updateData.day_of_month = data.day_of_month
  if (data.paid_by !== undefined) updateData.paid_by = data.paid_by
  if (data.scope !== undefined) updateData.scope = data.scope
  if (data.payment_method_id !== undefined) {
    ;(updateData as Record<string, unknown>).payment_method_id = data.payment_method_id
  }
  if (data.frequency !== undefined) {
    updateData.frequency = data.frequency
    updateData.frequency_months = data.frequency === 'yearly' ? (data.frequency_months ?? null) : null
  } else if (data.frequency_months !== undefined) {
    updateData.frequency_months = data.frequency_months
  }
  if (data.is_active !== undefined) {
    updateData.is_active = data.is_active
    if (data.is_active === true) {
      updateData.active_since = new Date().toISOString()
    }
  }

  if (Object.keys(updateData).length === 0) throw new Error('No fields to update')
  return updateData
}

export async function fetchRecurring(scope: ScopeFilter = 'all'): Promise<RecurringTransaction[]> {
  let query = supabase
    .from('recurring_transactions')
    .select('*')
    .order('created_at', { ascending: true })

  if (scope !== 'all') {
    query = query.eq('scope', scope)
  }

  const { data: recurring } = await query

  if (!recurring?.length) return []

  const { profileMap, categoryMap } = await fetchMaps()

  return recurring.map(r => mapRecurringTransaction(r, profileMap, categoryMap))
}

export async function createRecurring(data: {
  name: string
  amount: number
  currency?: Currency
  category_id?: string | null
  day_of_month: number
  paid_by: string
  scope?: 'household' | 'personal'
  frequency?: Frequency
  frequency_months?: number[] | null
  payment_method_id?: string | null
}) {
  const { data: { user } } = await supabase.auth.getUser()
  if (!user) throw new Error('Unauthorized')

  const insertData: Record<string, unknown> = {
    name: data.name,
    amount: data.amount,
    currency: data.currency || 'JPY',
    category_id: data.category_id || null,
    day_of_month: data.day_of_month,
    paid_by: data.paid_by,
    created_by: user.id,
    scope: data.scope || 'household',
    frequency: data.frequency || 'monthly',
    frequency_months: data.frequency === 'yearly' ? data.frequency_months : null,
  }
  if (data.payment_method_id !== undefined) {
    insertData.payment_method_id = data.payment_method_id
  }

  // payment_method_id is a new column not yet in generated types, hence the cast.
  const { error } = await (supabase as unknown as { from: (t: string) => { insert: (v: unknown) => Promise<{ error: { message: string } | null }> } })
    .from('recurring_transactions')
    .insert(insertData)

  if (error) throw new Error(error.message)
}

export type RecurringUpdateData = Partial<Pick<RecurringTransactionUpdate, 'name' | 'amount' | 'currency' | 'category_id' | 'day_of_month' | 'paid_by' | 'scope' | 'is_active' | 'frequency' | 'frequency_months'>> & {
  payment_method_id?: string | null
}

export async function updateRecurring(
  id: string,
  data: RecurringUpdateData,
) {
  const updateData = buildRecurringUpdateData(data)

  const { error } = await supabase
    .from('recurring_transactions')
    .update(updateData)
    .eq('id', id)

  if (error) throw new Error(error.message)
}

export async function deleteRecurring(id: string) {
  const { error } = await supabase
    .from('recurring_transactions')
    .delete()
    .eq('id', id)

  if (error) throw new Error(error.message)
}
